// Activity type enum
export type ActivityType = 
  | 'login'
  | 'logout'
  | 'project_created'
  | 'project_updated'
  | 'project_deleted'
  | 'schedule_uploaded'
  | 'schedule_updated'
  | 'report_generated'
  | 'work_package_created'
  | 'work_package_updated'
  | 'progress_updated'
  | 'document_uploaded'
  | 'comment_added'
  | 'user_invited'
  | 'user_role_changed';

// Activity log interface
export interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  userRole?: string;
  type: ActivityType;
  timestamp: string;
  details: {
    message: string;
    projectId?: string;
    projectName?: string;
    scheduleId?: string;
    scheduleName?: string;
    workPackageId?: string;
    workPackageName?: string;
    documentId?: string;
    documentName?: string;
    targetUserId?: string;
    targetUserName?: string;
    [key: string]: any;
  };
  ipAddress?: string;
}

// Mock activity logs
const MOCK_ACTIVITY_LOGS: ActivityLog[] = [
  {
    id: '1',
    userId: 'user-001',
    userName: 'Jane Doe',
    userRole: 'project_manager',
    type: 'project_created',
    timestamp: '2025-06-19T08:30:00Z',
    details: {
      message: 'Created new project "City Center Development"',
      projectId: 'project-001',
      projectName: 'City Center Development'
    },
    ipAddress: '192.168.1.1'
  },
  {
    id: '2',
    userId: 'user-002',
    userName: 'Robert Chen',
    userRole: 'scheduler',
    type: 'schedule_uploaded',
    timestamp: '2025-06-19T09:15:00Z',
    details: {
      message: 'Uploaded schedule "Initial Plan" to project "City Center Development"',
      projectId: 'project-001',
      projectName: 'City Center Development',
      scheduleId: 'schedule-001',
      scheduleName: 'Initial Plan'
    },
    ipAddress: '192.168.1.2'
  },
  {
    id: '3',
    userId: 'user-003',
    userName: 'John Smith',
    userRole: 'project_manager',
    type: 'work_package_created',
    timestamp: '2025-06-19T10:45:00Z',
    details: {
      message: 'Created work package "Foundation Work" in project "City Center Development"',
      projectId: 'project-001',
      projectName: 'City Center Development',
      workPackageId: 'wp-001',
      workPackageName: 'Foundation Work'
    },
    ipAddress: '192.168.1.3'
  },
  {
    id: '4',
    userId: 'user-004',
    userName: 'Sarah Williams',
    userRole: 'field_engineer',
    type: 'progress_updated',
    timestamp: '2025-06-19T14:20:00Z',
    details: {
      message: 'Updated progress on work package "Foundation Work" to 45%',
      projectId: 'project-001',
      projectName: 'City Center Development',
      workPackageId: 'wp-001',
      workPackageName: 'Foundation Work'
    },
    ipAddress: '192.168.1.4'
  },
  {
    id: '5',
    userId: 'user-005',
    userName: 'Emily Johnson',
    userRole: 'field_engineer',
    type: 'document_uploaded',
    timestamp: '2025-06-19T15:10:00Z',
    details: {
      message: 'Uploaded document "Site Survey Report" to project "City Center Development"',
      projectId: 'project-001',
      projectName: 'City Center Development',
      documentId: 'doc-001',
      documentName: 'Site Survey Report'
    },
    ipAddress: '192.168.1.5'
  },
  {
    id: '6',
    userId: 'user-001',
    userName: 'Jane Doe',
    userRole: 'project_manager',
    type: 'user_invited',
    timestamp: '2025-06-19T16:30:00Z',
    details: {
      message: 'Invited user "Michael Brown" to project "City Center Development" as "subcontractor"',
      projectId: 'project-001',
      projectName: 'City Center Development',
      targetUserId: 'user-006',
      targetUserName: 'Michael Brown'
    },
    ipAddress: '192.168.1.1'
  },
  {
    id: '7',
    userId: 'user-003',
    userName: 'John Smith',
    userRole: 'project_manager',
    type: 'report_generated',
    timestamp: '2025-06-19T17:45:00Z',
    details: {
      message: 'Generated "Executive Summary" report for project "City Center Development"',
      projectId: 'project-001',
      projectName: 'City Center Development'
    },
    ipAddress: '192.168.1.3'
  }
];

/**
 * Fetch activity logs with optional filtering
 */
export const fetchActivityLogs = async (
  filters?: {
    userId?: string;
    projectId?: string;
    type?: ActivityType | ActivityType[];
    startDate?: string;
    endDate?: string;
    limit?: number;
  }
): Promise<ActivityLog[]> => {
  // In a real implementation, this would make an API call with the filters
  // For now, return mock data with basic filtering
  return new Promise((resolve) => {
    setTimeout(() => {
      let filteredLogs = [...MOCK_ACTIVITY_LOGS];
      
      if (filters) {
        if (filters.userId) {
          filteredLogs = filteredLogs.filter(log => log.userId === filters.userId);
        }
        
        if (filters.projectId) {
          filteredLogs = filteredLogs.filter(log => log.details.projectId === filters.projectId);
        }
        
        if (filters.type) {
          const types = Array.isArray(filters.type) ? filters.type : [filters.type];
          filteredLogs = filteredLogs.filter(log => types.includes(log.type));
        }
        
        if (filters.startDate) {
          const startDate = new Date(filters.startDate);
          filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= startDate);
        }
        
        if (filters.endDate) {
          const endDate = new Date(filters.endDate);
          filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= endDate);
        }
        
        if (filters.limit && filters.limit > 0) {
          filteredLogs = filteredLogs.slice(0, filters.limit);
        }
      }
      
      // Sort by timestamp descending (newest first)
      filteredLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      resolve(filteredLogs);
    }, 300);
  });
};

/**
 * Log a new activity
 */
export const logActivity = async (activity: Omit<ActivityLog, 'id' | 'timestamp' | 'ipAddress'>): Promise<ActivityLog> => {
  // In a real implementation, this would make an API call
  // For now, just simulate adding to the mock data
  return new Promise((resolve) => {
    setTimeout(() => {
      const newActivity: ActivityLog = {
        ...activity,
        id: `${MOCK_ACTIVITY_LOGS.length + 1}`,
        timestamp: new Date().toISOString(),
        ipAddress: '192.168.1.10'
      };
      
      MOCK_ACTIVITY_LOGS.push(newActivity);
      resolve(newActivity);
    }, 200);
  });
};

// Export default and named exports
export default {
  fetchActivityLogs,
  logActivity
};
